import { goodLuckCoreEntitiesGameRoleEnum } from "@/shared/api/generated-kubb/types/goodLuck/core/entities/GameRole";
import type { GoodLuckCoreEntitiesGameRole } from "@/shared/api/generated-kubb/types/goodLuck/core/entities/GameRole";
import type { LolRoleKey } from "@/shared/ui/data-display/role-icon";

/**
 * Игровые роли для форм регистрации.
 *
 * Значения берутся ИЗ СГЕНЕРИРОВАННОГО Kubb-энума, а не пишутся руками: бэкенд ждёт
 * `Top/Jungle/Mid/Adc/Support` именно в таком регистре, и на расхождении регистров
 * в проекте уже ловили баги. Если бэкенд переименует роль — здесь сломается сборка,
 * а не поведение в проде.
 */
export type GameRoleValue = GoodLuckCoreEntitiesGameRole;

/**
 * Значение-заглушка для пункта «Любая роль».
 *
 * Radix Select не принимает пустую строку как значение пункта, поэтому «отсутствие
 * роли» приходится выражать токеном. На бэкенд он уходить НЕ должен: `PATCH
 * /tournaments/{id}/my-registration` разбирает роль через Enum.TryParse и на
 * неизвестном значении отвечает 400. Разворачивать через {@link roleToApi}.
 */
export const ANY_ROLE = "Any" as const;

export type RoleSelectValue = GameRoleValue | typeof ANY_ROLE;

/** Ключ иконки роли (нижний регистр) — RoleIcon принимает свой набор значений. */
const ROLE_ICON: Record<GameRoleValue, LolRoleKey> = {
  Top: "top",
  Jungle: "jungle",
  Mid: "mid",
  Adc: "adc",
  Support: "support",
};

/** Ключ перевода в неймспейсе `Tournaments.actions`. */
const ROLE_LABEL_KEY: Record<GameRoleValue, string> = {
  Top: "roleTop",
  Jungle: "roleJungle",
  Mid: "roleMid",
  Adc: "roleAdc",
  Support: "roleSupport",
};

export interface RoleOption {
  value: GameRoleValue;
  icon: LolRoleKey;
  labelKey: string;
}

/**
 * Список ролей для выпадающих списков. Порядок — как в энуме бэкенда
 * (Top → Jungle → Mid → Adc → Support), он же привычный порядок линий.
 */
export const ROLE_OPTIONS: readonly RoleOption[] = Object.values(
  goodLuckCoreEntitiesGameRoleEnum,
).map((value) => ({
  value,
  icon: ROLE_ICON[value],
  labelKey: ROLE_LABEL_KEY[value],
}));

/**
 * Значение селекта → значение для API. «Любая» и пустой выбор превращаются в
 * `undefined`: бэкенд трактует отсутствие роли как «готов на любую линию».
 */
export function roleToApi(value: RoleSelectValue | ""): GameRoleValue | undefined {
  if (!value || value === ANY_ROLE) return undefined;
  return value;
}

/**
 * Значение с сервера → значение селекта. Пустая роль показывается как «Любая»,
 * иначе селект остался бы без выбранного пункта.
 *
 * Регистр приводим сами: бэкенд разбирает роли регистронезависимо и в старых
 * записях встречаются варианты вроде `adc`, а Radix сравнивает значения строго.
 */
export function roleFromApi(value: string | null | undefined): RoleSelectValue {
  if (!value) return ANY_ROLE;
  const match = ROLE_OPTIONS.find(
    (option) => option.value.toLowerCase() === value.toLowerCase(),
  );
  return match?.value ?? ANY_ROLE;
}
